<svelte:options runes={false} />
<script>
  // The tabbed deployed-strategy workspace (U11/KTD10) — a purpose-built, hedge-fund-grade monitoring surface,
  // NOT a patched Backtester layout. It owns a persistent header (name · mode pill · lifecycle status ·
  // SSE-health · headline NAV) that stays visible across every tab so an operator never loses safety-critical
  // state while drilling in, plus a tab bar whose bodies are the U12/U12b/U14b/U12-params/U14 components. Tabs
  // are LAZY: a tab's body (and its data fetch) only mounts when selected, keeping the surface light.
  //
  // The host (TradingDashboard) still owns the authoritative refetch + SSE + lifecycle actions; this
  // shell is presentation + tab routing. It reuses shared LEAF components (LiveStatusSurface, PerformancePanel,
  // chart primitives) but the container/layout is fresh and AutoTrade-specific.
  import { activeTab, selectedStrategyTarget } from '../../lib/autotradeStore.js';
  import { targetOf, targetBadge } from '../../lib/runContext.js';
  import { formatCurrency } from '../../lib/store.js';
  import { formatQuoteAmount } from '../../lib/quoteCurrency.js';
  import { selectedOrgId, availableOrgs } from '../../lib/orgContext.js';
  import { recentBarCounts } from '../../lib/strategyErrors.js';
  import { fetchIntegrityOverall, statusTone } from '../../lib/integrity.js';
  import { nextRovingIndex } from '../../lib/rovingIndex.js';
  import api from '../../lib/api.js';

  import LiveStatusSurface from './LiveStatusSurface.svelte';
  import SymbolUniverse from '../shared/SymbolUniverse.svelte';
  import OrgSingleSelect from './OrgSingleSelect.svelte';
  import OverviewTab from './tabs/OverviewTab.svelte';
  import CandlesSignalsTab from './tabs/CandlesSignalsTab.svelte';
  import IntegrityTab from './tabs/IntegrityTab.svelte';
  import TcaTab from './tabs/TcaTab.svelte';
  import DecisionsTab from './tabs/DecisionsTab.svelte';
  import ExecutionTab from './tabs/ExecutionTab.svelte';
  import TwapTab from './tabs/TwapTab.svelte';
  import BookTab from './tabs/BookTab.svelte';
  import ErrorsTab from './tabs/ErrorsTab.svelte';
  import AlertsTab from './tabs/AlertsTab.svelte';
  import TradesTab from './tabs/TradesTab.svelte';
  import ParametersTab from './tabs/ParametersTab.svelte';
  import AllocationTab from './tabs/AllocationTab.svelte';

  // The selected strategy snapshot + its authoritative performance result (from the host's refetch).
  export let strategy = null;
  export let status = null;
  export let result = null;
  export let loading = false;
  export let error = null;
  export let actionBusy = false;
  // Whether the SELECTED strategy trades real money (the host computes it from the persisted target — safety).
  export let isLive = false;

  // Lifecycle action handlers (the host owns the API call + refetch). Passed through to the Overview tab.
  export let onStart = () => {};
  export let onPause = () => {};
  export let onResume = () => {};
  export let onStop = () => {};
  export let onRemove = () => {};
  export let onGoLive = null; // the venue-promotion opener (Paper context only; null on Live — D4)
  export let canGoLive = false;
  export let onRetry = () => {};

  const TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'candles', label: 'Candles & Signals' },
    { key: 'integrity', label: 'Integrity' },
    { key: 'tca', label: 'TCA' },
    { key: 'decisions', label: 'Decisions' },
    { key: 'execution', label: 'Execution' },
    { key: 'twap', label: 'TWAP' },
    { key: 'book', label: 'Book' },
    { key: 'errors', label: 'Errors' },
    { key: 'alerts', label: 'Alerts' },
    { key: 'trades', label: 'Trades' },
    { key: 'parameters', label: 'Parameters' },
    { key: 'allocation', label: 'Allocation' },
  ];

  // Tab badges (#3): the MOST-RECENT-BAR error/alert counts (server-derived window = rows at/after the latest
  // persisted bar). The badge resets as a newer bar advances the window. Re-fetched when (strategy, org) changes
  // OR the host bumps the strategy's bar/refetch signal (lastBarTime/barCount) so a new tick refreshes the count.
  $: workspaceStrategyId = strategy?.strategyId ?? null;
  let countsStore = recentBarCounts(null, null);
  let countsLastKey = '';
  $: {
    // Key on (strategy, org, latest-bar) — a new bar (lastBarTime/barCount advancing) re-reads the window.
    const key = `${workspaceStrategyId}|${$selectedOrgId}|${strategy?.lastBarTime ?? ''}|${strategy?.barCount ?? 0}`;
    if (key !== countsLastKey && workspaceStrategyId) {
      countsLastKey = key;
      countsStore = recentBarCounts(workspaceStrategyId, $selectedOrgId, key);
    }
  }
  $: errorCount = $countsStore.errorCount ?? 0;
  $: alertCount = $countsStore.alertCount ?? 0;
  function badgeText(n) { return n > 99 ? '99+' : String(n); }

  // Integrity tab dot (TASK-K2T-8): the worst invariant-check status (server-computed `overall`),
  // visible from ANY tab so a red NAV/book residual is never hidden behind an unvisited tab. Same
  // refresh key as the badge counts (strategy | org | latest bar) — re-fetched once per bar, not per
  // tick. A response landing after the key moved is dropped (stale-scope rule). Null (fetch failed /
  // no data) renders no dot — never a fake green.
  let integrityOverall = null;
  let integrityLastKey = '';
  $: {
    const key = `${workspaceStrategyId}|${$selectedOrgId}|${strategy?.lastBarTime ?? ''}|${strategy?.barCount ?? 0}`;
    if (key !== integrityLastKey && workspaceStrategyId) {
      integrityLastKey = key;
      fetchIntegrityOverall(workspaceStrategyId, $selectedOrgId).then((overall) => {
        if (integrityLastKey === key) integrityOverall = overall;
      });
    }
  }
  $: integrityDotTone = integrityOverall ? statusTone(integrityOverall) : null;

  // Target pill (ADR-0011 → ADR-0040): derives from the strategy's PERSISTED target {venue, mode} — LIVE only
  // for real money; PAPER / SANDBOX otherwise; UNKNOWN (fail-closed) when the snapshot cannot be classified.
  $: modeLabel = targetBadge($selectedStrategyTarget ?? targetOf(strategy));
  // Headline NAV is the latest persisted dollar mark; before the FIRST bar runs, the allocated capital IS the
  // strategy's NAV — never the metrics' netAssetValue, which is a 1.0-based TWR INDEX (formatting that as
  // currency is what showed "$1.00" on a freshly-started live strategy).
  $: headlineNav = strategy?.nav ?? (strategy?.startingCash > 0 ? strategy.startingCash : null);

  // ---- Header ORG chooser (single-select, Paper & Live). A strategy is per-org; the header lists THIS
  //      strategy's participating orgs and lets the operator monitor one at a time. Selecting an org sets the
  //      global selectedOrgId, which the host (TradingDashboard) reacts to with an authoritative refetch — so
  //      every tab + the headline NAV re-scope to the chosen org. Exactly one org is always selected.
  let participatingOrgs = []; // [{ orgId, orgName }] for the current strategy
  let orgFetchKey = '';
  const orgName = (orgId) => $availableOrgs.find((o) => o.orgId === orgId)?.orgName ?? orgId;

  $: {
    // Re-fetch the strategy's participating orgs when the strategy changes (keyed on its id). The fetch uses the
    // currently-selected org only to satisfy the request's scope param; the response lists all participating orgs.
    const key = workspaceStrategyId ?? '';
    if (key && key !== orgFetchKey) {
      orgFetchKey = key;
      loadParticipatingOrgs(key, $selectedOrgId);
    } else if (!key) {
      participatingOrgs = [];
    }
  }

  async function loadParticipatingOrgs(strategyId, at) {
    try {
      const slices = (await api.getOrgSlices(strategyId, at)) ?? [];
      const orgs = slices.map((s) => ({ orgId: s.orgId, orgName: orgName(s.orgId) }));
      if (orgFetchKey !== strategyId) return; // a newer strategy superseded this fetch
      participatingOrgs = orgs;
      // Keep the global selection consistent: if the active org isn't one this strategy participates in, default
      // to the first participating org (so the workspace never shows an org the strategy isn't in).
      if (orgs.length > 0 && !orgs.some((o) => o.orgId === $selectedOrgId)) {
        selectedOrgId.set(orgs[0].orgId);
      }
    } catch (err) {
      // Non-fatal: fall back to no header chooser (the workspace still works on the active org).
      if (orgFetchKey === strategyId) participatingOrgs = [];
    }
  }

  // The single-select binds to the global selectedOrgId via this proxy so a pick re-scopes the workspace.
  $: headerSelectedOrg = $selectedOrgId;
  function onHeaderOrgChange(orgId) {
    if (orgId && orgId !== $selectedOrgId) {
      selectedOrgId.set(orgId);
    }
  }

  // ---- Export Data (TASK-20260721 item 7): one click downloads the server-built ZIP (returns.csv,
  //      trades.csv, candles_signals.csv + README), scoped to the selected org like every other read.
  let exportBusy = false;
  let exportError = null;
  async function onExportData() {
    if (exportBusy || !workspaceStrategyId) return;
    exportBusy = true;
    exportError = null;
    try {
      const { blob, filename } = await api.exportStrategyData(workspaceStrategyId, $selectedOrgId);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      // Deferred revoke (diff-review F-5): Safari has cancelled downloads whose blob URL is revoked
      // synchronously after click — give the download time to attach before releasing the URL.
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (err) {
      // Surface the failure (diff-review F-6): a silent console error looks like a dead button.
      console.error('Export failed:', err);
      exportError = 'Export failed — check your access to the selected org and try again.';
    } finally {
      exportBusy = false;
    }
  }

  function selectTab(key) {
    activeTab.set(key);
  }
  function onTabKeydown(e, key, i) {
    // Roving-tab keyboard nav (left/right/home/end) across the tab bar (a11y) — shared helper.
    const next = nextRovingIndex(e.key, i, TABS.length);
    if (next === null) return;
    e.preventDefault();
    selectTab(TABS[next].key);
  }
</script>

<div class="at-workspace" class:live={isLive}>
  <!-- Persistent header: stays visible across every tab (the safety-critical state an operator must never lose). -->
  <header class="at-ws-header">
    <div class="at-ws-identity">
      <div class="at-ws-identity-row">
        <h3 class="at-ws-name">{strategy?.name ?? strategy?.strategy ?? 'Strategy'}</h3>
        <span class="at-ws-mode-pill" class:live={modeLabel === 'LIVE'}>{modeLabel}</span>
        {#if participatingOrgs.length > 0}
          <OrgSingleSelect selected={headerSelectedOrg} available={participatingOrgs} on:change={(e) => onHeaderOrgChange(e.detail)} />
        {/if}
      </div>
      <!-- TASK-20260823: the traded universe collapses to ONE row (a 400-name self-universe panel used to be a
           ' · '-joined paragraph here) and expands inline on "+N more". -->
      <SymbolUniverse symbols={strategy?.symbols} variant="inline" label="Traded pairs" />
    </div>
    <div class="at-ws-actions">
      <button
        type="button"
        class="at-ws-export"
        title="Export Data — downloads returns, trades and candles/signals as CSV (ZIP)"
        aria-label="Export strategy data as CSV ZIP"
        disabled={exportBusy}
        on:click={onExportData}>
        {#if exportBusy}
          <span class="at-ws-export-busy" aria-hidden="true"></span>
        {:else}
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
        {/if}
      </button>
      <div class="at-ws-nav">
        <span class="at-ws-nav-label">NAV</span>
        <!-- ADR-0014: a strategy's NAV renders in ITS OWN quote currency (₿/Ξ/USDC-suffixed; USD as before). -->
        <span class="at-ws-nav-value">{headlineNav != null ? formatQuoteAmount(headlineNav, strategy?.quoteCurrency ?? 'USD') : '—'}</span>
      </div>
    </div>
  </header>

  <!-- SSE-health + lifecycle status surface (shared leaf), always visible. -->
  <LiveStatusSurface
    {status}
    lastBarTime={strategy?.lastBarTime}
    barCount={strategy?.barCount ?? 0}
    frequencySeconds={strategy?.frequencySeconds ?? 0}
    strategyId={strategy?.strategyId}
    strategyName={strategy?.name ?? strategy?.strategy ?? null} />

  {#if error}
    <div class="alert alert-danger at-panel-error" role="alert">
      {error}
      <button type="button" class="btn btn-ghost btn-sm" on:click={onRetry}>Retry</button>
    </div>
  {/if}

  {#if exportError}
    <div class="alert alert-danger at-panel-error" role="alert">
      {exportError}
      <button type="button" class="btn btn-ghost btn-sm" on:click={() => (exportError = null)}>Dismiss</button>
    </div>
  {/if}

  <!-- Tab bar. -->
  <div class="at-tab-bar" role="tablist" aria-label="Strategy workspace tabs">
    {#each TABS as t, i}
      <button
        type="button"
        role="tab"
        id={`at-tab-${t.key}`}
        class="at-tab"
        class:active={$activeTab === t.key}
        aria-selected={$activeTab === t.key}
        aria-controls={`at-tabpanel-${t.key}`}
        tabindex={$activeTab === t.key ? 0 : -1}
        on:click={() => selectTab(t.key)}
        on:keydown={(e) => onTabKeydown(e, t.key, i)}>
        {t.label}
        {#if t.key === 'integrity' && integrityDotTone && integrityDotTone !== 'na'}
          <span class="at-tab-dot at-tab-dot-{integrityDotTone}"
            aria-label={`integrity checks ${integrityDotTone === 'ok' ? 'green' : integrityDotTone === 'warn' ? 'have warnings' : 'are failing'}`}
            title={`Integrity checks: ${integrityOverall}`}></span>
        {/if}
        {#if t.key === 'errors' && errorCount > 0}
          <span class="at-tab-badge" aria-label={`${errorCount} errors in the most recent bar`}>{badgeText(errorCount)}</span>
        {/if}
        {#if t.key === 'alerts' && alertCount > 0}
          <span class="at-tab-badge at-tab-badge-alert" aria-label={`${alertCount} alerts in the most recent bar`}>{badgeText(alertCount)}</span>
        {/if}
      </button>
    {/each}
  </div>

  <!-- Lazy tab bodies: only the active tab mounts (and fetches its data). -->
  <div class="at-tab-panel" role="tabpanel" id={`at-tabpanel-${$activeTab}`} aria-labelledby={`at-tab-${$activeTab}`}>
    {#if $activeTab === 'overview'}
      <OverviewTab {strategy} {status} {result} {loading} {actionBusy}
        {onStart} {onPause} {onResume} {onStop} {onRemove} {onGoLive} {canGoLive} />
    {:else if $activeTab === 'candles'}
      <CandlesSignalsTab {strategy} {status} {isLive} />
    {:else if $activeTab === 'integrity'}
      <IntegrityTab {strategy} {status} />
    {:else if $activeTab === 'tca'}
      <TcaTab {strategy} {status} />
    {:else if $activeTab === 'decisions'}
      <DecisionsTab {strategy} {status} {result} />
    {:else if $activeTab === 'execution'}
      <ExecutionTab {strategy} {status} />
    {:else if $activeTab === 'twap'}
      <TwapTab {strategy} {status} {isLive} />
    {:else if $activeTab === 'book'}
      <BookTab {strategy} {status} />
    {:else if $activeTab === 'errors'}
      <ErrorsTab {strategy} {status} />
    {:else if $activeTab === 'alerts'}
      <AlertsTab {strategy} {status} />
    {:else if $activeTab === 'trades'}
      <TradesTab {strategy} {result} {loading} />
    {:else if $activeTab === 'parameters'}
      <ParametersTab {strategy} {status} {result} />
    {:else if $activeTab === 'allocation'}
      <AllocationTab {strategy} {status} {isLive} />
    {/if}
  </div>
</div>

<style>
  .at-workspace {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  .at-ws-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.75rem 1rem;
    border: 1px solid var(--border-subtle, #1e1f22);
    border-radius: 8px;
    background: var(--surface-1, rgba(255, 255, 255, 0.02));
  }
  /* basis 0: the identity column's width comes from the header, never from the pair strip's content. */
  .at-ws-identity { display: flex; flex-direction: column; gap: 0.4rem; flex: 1 1 0; min-width: 0; }
  .at-ws-identity-row { display: flex; align-items: center; gap: 0.6rem; flex-wrap: wrap; }
  .at-ws-name { margin: 0; font-size: 1.05rem; font-weight: 700; color: var(--text-primary, #e8e9ea); }
  .at-ws-mode-pill {
    font-size: 0.65rem; font-weight: 700; letter-spacing: 0.05em;
    padding: 0.15rem 0.45rem; border-radius: 4px;
    background: var(--surface-2, rgba(120, 160, 255, 0.12)); color: var(--text-secondary, #b5b6b8);
  }
  .at-ws-mode-pill.live { background: rgba(240, 90, 70, 0.18); color: #f0594a; }
  .at-ws-actions { display: flex; flex-direction: row; align-items: center; gap: 0.75rem; flex-shrink: 0; }
  .at-ws-nav { display: flex; flex-direction: column; align-items: flex-end; }
  .at-ws-export {
    display: inline-flex; align-items: center; justify-content: center;
    width: 1.9rem; height: 1.9rem; padding: 0;
    background: transparent; border: 1px solid var(--border-subtle, #1e1f22); border-radius: 6px;
    color: var(--text-muted, #8a8b8d); cursor: pointer;
  }
  .at-ws-export:hover:not(:disabled) { color: var(--text-primary, #e8e9ea); border-color: var(--text-muted, #8a8b8d); }
  .at-ws-export:disabled { opacity: 0.6; cursor: default; }
  .at-ws-export-busy {
    width: 0.85rem; height: 0.85rem; border-radius: 50%;
    border: 2px solid var(--text-muted, #8a8b8d); border-top-color: transparent;
    animation: at-ws-export-spin 0.8s linear infinite;
  }
  @keyframes at-ws-export-spin { to { transform: rotate(360deg); } }
  .at-ws-nav-label { font-size: 0.65rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--text-muted, #8a8b8d); }
  .at-ws-nav-value { font-size: 1.1rem; font-weight: 700; color: var(--text-primary, #e8e9ea); font-variant-numeric: tabular-nums; }

  .at-tab-bar {
    display: flex;
    gap: 0.25rem;
    border-bottom: 1px solid var(--border-subtle, #1e1f22);
    overflow-x: auto;
  }
  .at-tab {
    appearance: none;
    background: transparent;
    border: none;
    border-bottom: 2px solid transparent;
    padding: 0.55rem 0.9rem;
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--text-muted, #8a8b8d);
    cursor: pointer;
    white-space: nowrap;
  }
  .at-tab:hover { color: var(--text-secondary, #b5b6b8); }
  .at-tab.active {
    color: var(--text-primary, #e8e9ea);
    border-bottom-color: var(--accent, #3c78f0);
  }
  .at-workspace.live .at-tab.active { border-bottom-color: #f0594a; }
  .at-tab-badge {
    display: inline-block; margin-left: 0.35rem; padding: 0 0.35rem; min-width: 1.1rem;
    font-size: 0.7rem; font-weight: 700; line-height: 1.25rem; text-align: center;
    color: #fff; background: var(--danger, #e0533a); border-radius: 999px;
  }
  /* Alerts (Info/Warning) badge — amber, distinct from the red Errors badge. */
  .at-tab-badge-alert { background: var(--warning, #d8a000); color: #1a1a1a; }
  /* Integrity dot (TASK-K2T-8): the worst invariant-check status, visible from any tab. The tooltip/
     aria-label carries the words — the dot is a cue, never the only channel (no color-only encoding). */
  .at-tab-dot {
    display: inline-block; margin-left: 0.35rem; width: 0.5rem; height: 0.5rem;
    border-radius: 50%; vertical-align: middle;
  }
  .at-tab-dot-ok { background: var(--success, #1aa260); }
  .at-tab-dot-warn { background: var(--warning, #d8a000); }
  .at-tab-dot-error { background: var(--danger, #e0533a); }
  .at-tab-panel { min-height: 200px; }
</style>
